import { Button, IconButton, Stack, TextField } from '@mui/material'
import React from 'react'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'

import { PasswordTextField } from 'components/common/PasswordTextField'
import Icon from 'components/Icon'
import { StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'
import { useTranslation } from 'hooks/use-translation'

export const ObservabilityToolHeaders: React.FC<{
  isCustomObservabilityTool?: boolean
}> = ({ isCustomObservabilityTool = false }) => {
  const { trans } = useTranslation()
  const { control, formState } = useFormContext<StatusCheckFormValues>()

  const { fields, append, remove } = useFieldArray({
    name: 'headers',
    control
  })

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      {fields.map((field, index) => (
        <Stack
          key={field.id}
          direction="row"
          spacing={2}
          sx={{ width: '100%' }}
        >
          <Controller
            control={control}
            name={`headers.${index}.key`}
            render={({ field: controllerRenderField }) => (
              <TextField
                autoComplete="off"
                data-testid={`observability-tool-authentication-header-key-input-${index}`}
                disabled={!isCustomObservabilityTool}
                {...controllerRenderField}
                error={Boolean(formState.errors.headers?.[index]?.key)}
                helperText={formState.errors.headers?.[index]?.key?.message}
                label={trans(
                  'forms.save_status_checks.observability_tool_headers.key.text_field.label'
                )}
                variant="outlined"
              />
            )}
          />
          <Controller
            control={control}
            name={`headers.${index}.value`}
            render={({ field: controllerRenderField }) => (
              <PasswordTextField
                {...controllerRenderField}
                autoComplete="off"
                data-testid={`observability-tool-authentication-header-value-input-${index}`}
                error={Boolean(formState.errors.headers?.[index]?.value)}
                helperText={formState.errors.headers?.[index]?.value?.message}
                label={
                  field.label ||
                  trans(
                    'forms.save_status_checks.observability_tool_headers.value.text_field.label'
                  )
                }
                variant="outlined"
              />
            )}
          />
          {/* Only custom observability tools are allowed to add/remove custom headers */}
          {isCustomObservabilityTool && (
            <IconButton
              color="error"
              data-testid={`observability-tool-authentication-remove-header-button-${index}`}
              size="large"
              onClick={() => remove(index)}
            >
              <Icon icon="circleTrash" />
            </IconButton>
          )}
        </Stack>
      ))}
      {/* Only custom observability tools are allowed to add/remove custom headers */}
      {isCustomObservabilityTool && (
        <Button
          data-testid="observability-tool-authentication-add-header-button"
          startIcon={<Icon icon="plus" />}
          sx={{ width: 'fit-content' }}
          variant="contained"
          onClick={() => append({ key: '', value: '' })}
        >
          {trans('forms.buttons.add_header.label')}
        </Button>
      )}
    </Stack>
  )
}
